import Joi from 'joi';
import { useSearchParams as useSearchParamsRouter } from 'react-router-dom';
// Constants
import { omit } from 'lodash';

/**
 * Removes properties with `null` or `undefined` values from an object.
 *
 * @param {Object} obj - The original object to be cleaned.
 * @returns {Object} A new object with all properties set to `null` or `undefined` removed.
 */
const cleanObject = (obj) => {
  const entries = Object.entries(obj);
  const filteredEntries = entries.filter(([, value]) => value != null);
  return Object.fromEntries(filteredEntries);
};

/**
 * @typedef {Object} SearchParams
 * @import { BU_ZOL, BU_QWERI } from "../../../../../constants"
 *
 * @property {('list'|'planning')} [display]
 * @property {('RemoteWorking'|'Leave')} [view]
 * @property {string} [year]
 * @property {string} [month]
 * @property {string} [excludedEndSlotId]
 * @property {string} [includedStartSlotId]
 * @property {('Pending'|'Accepted'|'Refused')} [status]
 * @property {string} [type]
 * @property {string} [resourceId]
 * @property {(BU_ZOL | BU_QWERI)} [businessUnit]
 */
const paramsSchema = Joi.object({
  display: Joi.string().valid('list', 'planning'),
  view: Joi.string().valid('RemoteWorking', 'Leave'),
  year: Joi.string(),
  month: Joi.string(),
  excludedEndSlotId: Joi.string().regex(/^\d+$/),
  includedStartSlotId: Joi.string().regex(/^\d+$/),
  status: Joi.string().valid('Pending', 'Accepted', 'Refused'),
  type: Joi.string(),
  resourceId: Joi.string(),
  businessUnit: Joi.string(),
});

const useSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParamsRouter();

  /**
   * Return every URL search params as a JS Object.
   *
   * @returns {SearchParams}
   *
   */
  const getSearchParams = () => {
    const params = {};

    searchParams.forEach((paramValue, param) => {
      params[param] = paramValue;
    });

    const validation = paramsSchema.validate(params, { abortEarly: false });

    if (validation.error) {
      // eslint-disable-next-line no-console
      console.error('Params from search params does not validate schema', validation.error.message);
      const paramKeysInError = validation.error.details.map(
        (errorDetail) => errorDetail.context.key,
      );
      return omit(params, paramKeysInError);
    }

    return params;
  };

  /**
   * Update URL search params with keeping previous params if needed.
   *
   * @param {Object} params
   * @param {boolean} [keepPreviousParams=false]
   */
  const updateSearchParams = (params, keepPreviousParams = false) => {
    let previousParams = {};

    if (keepPreviousParams) {
      previousParams = getSearchParams();
    }
    setSearchParams(cleanObject({ ...previousParams, ...params }));
  };

  return {
    getSearchParams,
    updateSearchParams,
  };
};

export default useSearchParams;
